import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper, Box, Typography, TextField, Button, MenuItem, Chip, Alert,
  TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import axios from '../../api/client';
import { formatAmount } from '../../utils/money';
import { sideLabel, balanceMagnitude } from '../../utils/ledger';
import ScrollTable from '../ScrollTable';
import EmptyState from '../EmptyState';

// What produced an entry, in words. sourceType is an enum written for the posting code, so
// 'contra' and 'management_fee' mean nothing to somebody reading their own ledger.
//
// Anything unmapped falls through to the raw value rather than to a blank: a new source type
// added to the posting code and not added here should look unfamiliar, not absent.
const SOURCE_LABELS = {
  invoice: 'Invoice',
  payment: 'Payment',
  allocation: 'Allocation',
  adjustment: 'Adjustment',
  expense: 'Expense',
  manual: 'Manual entry',
  landlord_payment: 'Landlord payment',
  opening_balance: 'Opening balance',
  closing: 'Year-end close',
  depreciation: 'Depreciation',
  management_fee: 'Commission',
  asset_purchase: 'Asset purchase',
  other_income: 'Other income',
  contra: 'Transfer'
};
const sourceLabel = (type) => SOURCE_LABELS[type] || type || '—';

// A balance with the side it sits on, exactly as the Chart of Accounts writes it. The sign
// is carried by Dr/Cr, never by a minus, because a statement says Dr/Cr and this column
// exists to be checked against one.
const Balance = ({ amount, account }) => {
  const side = sideLabel(amount, account);
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.75, fontVariantNumeric: 'tabular-nums' }}>
      <Box component="span">{formatAmount(balanceMagnitude(amount))}</Box>
      <Box component="span" sx={{ minWidth: 18, color: side === 'Dr' ? 'info.main' : 'warning.main', fontWeight: 600 }}>
        {side}
      </Box>
    </Box>
  );
};

const asDateInput = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * One account's activity, in order, with a running balance.
 *
 * `accountId` preselects an account — that is how the Chart of Accounts and the Trial
 * Balance drill into this. Opened on its own it starts with the picker empty and waits.
 */
const AccountLedger = ({ accountId: initialAccountId = '' }) => {
  const [accounts, setAccounts] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [accountId, setAccountId] = useState(initialAccountId);
  const [landlordId, setLandlordId] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/accounting/chart-of-accounts')
      .then(({ data: list }) => setAccounts(list || []))
      .catch(() => setAccounts([]));
    // Not fatal if it fails: the landlord filter is an extra way to cut the ledger, and a
    // user without landlords:view should still get the account's own ledger rather than an
    // error page. The dropdown simply stays empty.
    axios.get('/landlords')
      .then(({ data: list }) => setLandlords(list || []))
      .catch(() => setLandlords([]));
  }, []);

  const fetchLedger = useCallback(async (id, window, landlord = '') => {
    if (!id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const params = { accountId: id };
      if (window.from) params.from = window.from;
      if (window.to) params.to = window.to;
      if (landlord) params.landlordId = landlord;
      const { data: ledger } = await axios.get('/accounting/reports/account-ledger', { params });
      setData(ledger);
      // The server decides the window when none was given — the financial year to date, and
      // the fiscal year start is a setting rather than the calendar. Reflecting it back into
      // the boxes means the dates on screen are the dates that were used, instead of two
      // empty fields above a report covering a period nobody stated.
      setRange((r) => ({
        from: r.from || asDateInput(ledger.from),
        to: r.to || asDateInput(ledger.to)
      }));
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load this account ledger');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Preselected from another screen: load it without making the user press Apply, since
  // they have already said which account they meant by clicking its balance.
  useEffect(() => {
    if (initialAccountId) fetchLedger(initialAccountId, { from: '', to: '' });
  }, [initialAccountId, fetchLedger]);

  const handleExport = () => {
    if (!data) return;
    const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      [`${data.account.code} ${data.account.name}`,
        `${asDateInput(data.from)} to ${asDateInput(data.to)}`,
        // On the exported file too. A spreadsheet outlives the screen it came from, and one
        // landlord's slice of an account is indistinguishable from the whole of it once the
        // filter box is gone.
        data.landlord ? `Landlord: ${data.landlord.name}` : 'All landlords'],
      [],
      ['Date', 'Details', 'Source', 'Contra account', 'Debit', 'Credit', 'Balance'],
      ['', `Opening balance ${asDateInput(data.from)}`, '', '', '', '', data.opening.balance],
      ...data.lines.map((l) => [
        asDateInput(l.entryDate),
        l.lineMemo || l.entryMemo || '',
        sourceLabel(l.sourceType),
        l.contra.map((c) => `${c.code} ${c.name}`).join('; '),
        l.debit || '',
        l.credit || '',
        l.balance
      ]),
      ['', 'Closing balance', '', '', data.totals.debit, data.totals.credit, data.closing]
    ];
    const csv = rows.map((r) => r.map(cell).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger-${data.account.code}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const account = data?.account;

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5">Account Ledger</Typography>
          <Typography variant="body2" color="text.secondary">
            Every movement on one account, in order, with the account facing it.
          </Typography>
        </Box>
        {account && (
          <Box textAlign="right">
            <Typography variant="h6">{account.code} {account.name}</Typography>
            <Box display="flex" gap={0.75} justifyContent="flex-end" sx={{ mt: 0.5 }}>
              <Chip size="small" label={account.type} />
              {/* Said on the report itself, not only in the filter box. A scoped ledger is a
                  different report — its closing figure is not the account's — and a printed
                  or exported copy with no landlord on it would be taken for the whole
                  account by whoever reads it next. */}
              {data.landlord && (
                <Chip size="small" color="primary" label={data.landlord.name} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <TextField
          select
          label="Account"
          size="small"
          sx={{ minWidth: 260 }}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <MenuItem value="">Choose an account…</MenuItem>
          {accounts.map((a) => (
            <MenuItem key={a._id} value={a._id}>{a.code} — {a.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
          value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
        />
        <TextField
          type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
          value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
        />
        {/* Narrows the ledger to the entries belonging to one landlord — their rent, their
            costs, their payouts — using the same scope the trial balance and the landlord
            P&L use, so the three can be reconciled against each other. Agency-borne costs
            are left out by that scope: they are the agency's to bear, not the landlord's. */}
        <TextField
          select
          label="Landlord"
          size="small"
          sx={{ minWidth: 200 }}
          value={landlordId}
          onChange={(e) => setLandlordId(e.target.value)}
          helperText={landlordId ? 'This landlord’s entries only' : 'The whole agency'}
        >
          <MenuItem value="">All landlords</MenuItem>
          {landlords.map((l) => (
            <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" disabled={!accountId || loading} onClick={() => fetchLedger(accountId, range, landlordId)}>
          Apply
        </Button>
        <Box flexGrow={1} />
        <Button variant="outlined" disabled={!data} onClick={handleExport}>Export CSV</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* THE PAGE CHECKING ITSELF. The closing balance below is the opening figure plus every
          movement listed; `ledgerClosing` is the same account put to the same function the
          trial balance uses. If a ledger that is supposed to explain a balance disagrees
          with it, that is the single most important thing on the screen. */}
      {data && data.tiesToLedger === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This ledger adds up to {formatAmount(data.closing)} but the account stands at{' '}
          {formatAmount(data.ledgerClosing)} — a difference of{' '}
          {formatAmount(data.closing - data.ledgerClosing)}. The lines below do not explain
          the balance; something has reached the ledger that this report cannot see.
        </Alert>
      )}
      {data && data.tiesToLedger === true && data.lines.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {data.landlord
            // Naming the landlord matters here rather than being a nicety. A scoped closing
            // balance deliberately does NOT equal what the Chart of Accounts shows for this
            // account — that figure covers every landlord — so a bare "agrees with the trial
            // balance" would send somebody to compare it against a number it was never
            // meant to match, and report a fault when there is none.
            ? `Closing balance agrees with the trial balance scoped to ${data.landlord.name}. `
              + 'It is not the account’s full balance — that covers every landlord.'
            : 'Closing balance agrees with the trial balance.'}
        </Alert>
      )}
      {data && data.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing the first {data.limit} movements in this range. The running balance is
          correct for every line shown, but the closing figure is not the account's — narrow
          the dates to see the rest.
        </Alert>
      )}

      {!accountId && !loading && (
        <EmptyState
          title="Choose an account"
          description="Pick an account above to see what moved through it and where each figure came from."
        />
      )}

      {data && (
        <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Date</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Contra account</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Where the account stood before the first line below. Without it the running
                balance starts from nowhere and ties to nothing. */}
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell className="pin-left" sx={{ fontWeight: 600 }}>
                {asDateInput(data.from)}
              </TableCell>
              <TableCell colSpan={5} sx={{ fontWeight: 600 }}>Balance brought forward</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Balance amount={data.opening.balance} account={account} />
              </TableCell>
            </TableRow>

            {data.lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState compact title="Nothing moved through this account in this period" />
                </TableCell>
              </TableRow>
            )}

            {data.lines.map((line) => (
              <TableRow
                key={line.lineId}
                // A reversed entry still counts in the running balance — its mirror is
                // further down the page and cancels it — but a reader scanning for a figure
                // needs to see at a glance that it no longer stands on its own.
                sx={line.reversedByEntryId ? { opacity: 0.65 } : undefined}
              >
                <TableCell className="pin-left">
                  {new Date(line.entryDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {line.lineMemo || line.entryMemo || '—'}
                  {line.lineMemo && line.entryMemo && line.lineMemo !== line.entryMemo && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {line.entryMemo}
                    </Typography>
                  )}
                  {line.reversedByEntryId && (
                    <Chip size="small" label="Reversed" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                  {line.reversesEntryId && (
                    <Chip size="small" label="Reversal" color="default" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={sourceLabel(line.sourceType)} />
                </TableCell>
                {/* THE ANSWER TO "WHERE DID IT COME FROM" — the account on the other side of
                    this entry. Usually one; a transfer carrying a charge, or a receipt split
                    across several invoices, has more than one and every one of them is part
                    of the answer. */}
                <TableCell>
                  {line.contra.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {line.contra.map((c) => (
                        <Box key={c.accountId} display="flex" gap={0.75} alignItems="center">
                          <Chip size="small" label={c.code} color="info" sx={{ fontWeight: 600 }} />
                          <Typography variant="body2">{c.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {line.debit > 0 ? formatAmount(line.debit) : ''}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {line.credit > 0 ? formatAmount(line.credit) : ''}
                </TableCell>
                <TableCell align="right">
                  <Balance amount={line.balance} account={account} />
                </TableCell>
              </TableRow>
            ))}

            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell className="pin-left" sx={{ fontWeight: 700 }}>
                {asDateInput(data.to)}
              </TableCell>
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Closing balance</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(data.totals.debit)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(data.totals.credit)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <Balance amount={data.closing} account={account} />
              </TableCell>
            </TableRow>
          </TableBody>
        </ScrollTable>
      )}
    </Paper>
  );
};

export default AccountLedger;
